import { z } from "zod";

import { FieldMappingAiConfigSchema, FieldMappingSourceType } from "./hubspot";

export type { AiTestRequest, AiTestResponse, FieldMappingAiConfig } from "./hubspot";
export { AiTestResponseSchema } from "./hubspot";

// Re-export the shared source type enum so Salesforce consumers don't need the hubspot schema.
export { FieldMappingSourceType };

export {
	SALESFORCE_PRODUCTION_HOST,
	SALESFORCE_SANDBOX_HOST,
	SalesforceEnvironment,
	createSalesforceConnectionSchema,
	isValidSalesforceMyDomain,
	normalizeSalesforceMyDomain,
} from "./salesforceEnvironment";
export type { SalesforceConnectionFormValues } from "./salesforceEnvironment";

/** sObjects the Salesforce sync can write to. */
export enum SalesforceEntityType {
	CONTACT = "CONTACT",
	ACCOUNT = "ACCOUNT",
	LEAD = "LEAD",
	OPPORTUNITY = "OPPORTUNITY",
}

/**
 * Opportunity fields owned by the deal-config endpoint or by the sync's relationship linking.
 * `POST field-mappings` rejects them with a 400, so they are hidden from the opportunity
 * target-field picker.
 *
 * Unlike a calculated or read-only field these are perfectly writable — they simply lose to
 * the deal configuration, which fills them after the coercion pass. The picker says so
 * (`fieldMapping.reservedTooltip`) rather than calling them read-only.
 */
export const SALESFORCE_OPPORTUNITY_RESERVED_FIELD_KEYS: readonly string[] = [
	"Name",
	"StageName",
	"CloseDate",
	"OwnerId",
	"Amount",
	"RecordTypeId",
	"AccountId",
];

// ── Field metadata ───────────────────────────────────────────────────

export const SalesforceFieldOptionSchema = z.object({
	label: z.string(),
	value: z.coerce.string(),
});

export const SalesforceFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	/** Salesforce describe `type`: `string` / `picklist` / `reference` / `currency` / … */
	type: z.string(),
	isCustom: z.boolean().optional(),
	description: z.string().optional(),
	options: z.array(SalesforceFieldOptionSchema).optional(),
	/**
	 * Describe's `restrictedPicklist`. When true the org rejects any value outside `options`,
	 * so an AI mapping's fallback has to be one of them.
	 */
	optionsAreRestricted: z.boolean().optional(),
	isRequired: z.boolean().optional(),
	isReadOnly: z.boolean().optional(),
	isCalculated: z.boolean().optional(),
	isMulti: z.boolean().optional(),
	/** Describe's `length`; the sync truncates to it rather than letting the write fail. */
	maxLength: z.number().optional(),
});

export type SalesforceField = z.infer<typeof SalesforceFieldSchema>;

export const SalesforceFieldsResponseSchema = z.object({
	fields: z.array(SalesforceFieldSchema),
	entityType: z.string().optional(),
});

export type SalesforceFieldsResponse = z.infer<typeof SalesforceFieldsResponseSchema>;

// ── Source fields (the Signalist side of a mapping) ──────────────────

export const SalesforceSourceFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	type: z.string().optional(),
	category: z.string().optional(),
});

export type SalesforceSourceField = z.infer<typeof SalesforceSourceFieldSchema>;

/**
 * Keyed by sObject. `leadFields` carries prospect **and** organization fields because a
 * Salesforce `Lead` holds the person and the company on one record.
 */
export const SalesforceSourceFieldsResponseSchema = z.object({
	contactFields: z.array(SalesforceSourceFieldSchema),
	accountFields: z.array(SalesforceSourceFieldSchema),
	opportunityFields: z.array(SalesforceSourceFieldSchema).optional(),
	leadFields: z.array(SalesforceSourceFieldSchema).optional(),
});

export type SalesforceSourceFieldsResponse = z.infer<typeof SalesforceSourceFieldsResponseSchema>;

// ── Field mappings ───────────────────────────────────────────────────

export const SalesforceFieldMappingSchema = z.object({
	id: z.number(),
	entityType: z.nativeEnum(SalesforceEntityType),
	sourceType: z.nativeEnum(FieldMappingSourceType),
	sourceField: z.string(),
	salesforceField: z.string(),
	salesforceFieldLabel: z.string().nullish(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean(),
	aiConfig: FieldMappingAiConfigSchema.nullish(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export type SalesforceFieldMapping = z.infer<typeof SalesforceFieldMappingSchema>;

export const SalesforceFieldMappingsResponseSchema = z.array(SalesforceFieldMappingSchema);

export type SalesforceFieldMappingsResponse = z.infer<typeof SalesforceFieldMappingsResponseSchema>;

export const CreateSalesforceFieldMappingSchema = z.object({
	entityType: z.nativeEnum(SalesforceEntityType),
	sourceType: z.nativeEnum(FieldMappingSourceType).optional(),
	sourceField: z.string(),
	salesforceField: z.string(),
	salesforceFieldLabel: z.string().optional(),
	isCustomField: z.boolean().optional(),
	isActive: z.boolean().optional(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
});

export type CreateSalesforceFieldMapping = z.infer<typeof CreateSalesforceFieldMappingSchema>;

export const UpdateSalesforceFieldMappingSchema = CreateSalesforceFieldMappingSchema.partial().omit(
	{ entityType: true }
);

export type UpdateSalesforceFieldMapping = z.infer<typeof UpdateSalesforceFieldMappingSchema>;

export const BulkCreateSalesforceFieldMappingsSchema = z.object({
	mappings: z.array(CreateSalesforceFieldMappingSchema),
});

export type BulkCreateSalesforceFieldMappings = z.infer<
	typeof BulkCreateSalesforceFieldMappingsSchema
>;

// ── Owners & user mappings ───────────────────────────────────────────

export const SalesforceOwnerSchema = z.object({
	id: z.string(),
	name: z.string(),
	email: z.string(),
});

export type SalesforceOwner = z.infer<typeof SalesforceOwnerSchema>;

export const SalesforceOwnersResponseSchema = z.object({
	owners: z.array(SalesforceOwnerSchema),
});

export type SalesforceOwnersResponse = z.infer<typeof SalesforceOwnersResponseSchema>;

export const SalesforceUserMappingSchema = z.object({
	id: z.number(),
	userId: z.number(),
	salesforceOwnerId: z.string(),
	salesforceOwnerName: z.string().nullish(),
	userFirstName: z.string().nullish(),
	userLastName: z.string().nullish(),
	userEmail: z.string().nullish(),
});

export type SalesforceUserMapping = z.infer<typeof SalesforceUserMappingSchema>;

export const SalesforceUserMappingsResponseSchema = z.array(SalesforceUserMappingSchema);

export type SalesforceUserMappingsResponse = z.infer<typeof SalesforceUserMappingsResponseSchema>;

export const CreateSalesforceUserMappingSchema = z.object({
	userId: z.number(),
	salesforceOwnerId: z.string(),
	salesforceOwnerName: z.string().optional(),
});

export type CreateSalesforceUserMapping = z.infer<typeof CreateSalesforceUserMappingSchema>;

// ── Sync triggers ────────────────────────────────────────────────────

export enum SalesforceSyncTrigger {
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

export enum SalesforceSyncTriggerWithInherit {
	INHERIT = "INHERIT",
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

export const SalesforceCompanySyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(SalesforceSyncTrigger),
});

export type SalesforceCompanySyncTriggerResponse = z.infer<
	typeof SalesforceCompanySyncTriggerResponseSchema
>;

export const UpdateSalesforceCompanySyncTriggerSchema = z.object({
	trigger: z.nativeEnum(SalesforceSyncTrigger),
});

export type UpdateSalesforceCompanySyncTrigger = z.infer<
	typeof UpdateSalesforceCompanySyncTriggerSchema
>;

export const SalesforceListSyncTriggerResponseSchema = z.object({
	trigger: z.union([
		z.nativeEnum(SalesforceSyncTrigger),
		z.nativeEnum(SalesforceSyncTriggerWithInherit),
	]),
	effectiveTrigger: z.nativeEnum(SalesforceSyncTrigger).optional(),
	isInherited: z.boolean().optional(),
});

export type SalesforceListSyncTriggerResponse = z.infer<
	typeof SalesforceListSyncTriggerResponseSchema
>;

export const UpdateSalesforceListSyncTriggerSchema = z.object({
	trigger: z.nativeEnum(SalesforceSyncTriggerWithInherit),
});

export type UpdateSalesforceListSyncTrigger = z.infer<typeof UpdateSalesforceListSyncTriggerSchema>;

// ── OAuth ────────────────────────────────────────────────────────────

export const SalesforceOAuthConnectResponseSchema = z.object({
	url: z.string(),
});

export type SalesforceOAuthConnectResponse = z.infer<typeof SalesforceOAuthConnectResponseSchema>;

export const SalesforceOAuthStatusResponseSchema = z.object({
	connected: z.boolean(),
	/** The org's `instance_url` — the base of every REST/SOQL call, and of a record link. */
	apiDomain: z.string().nullish(),
	loginHost: z.string().nullish(),
	salesforceUserId: z.string().nullish(),
	salesforceOrgId: z.string().nullish(),
	/**
	 * Assumed, not disclosed: Salesforce returns no `expires_in`, so the backend derives this
	 * from `issued_at` plus an assumed session length. The connection tab must not present it
	 * as a fact the way the Dynamics tab can.
	 */
	tokenExpiry: z.string().nullish(),
});

export type SalesforceOAuthStatusResponse = z.infer<typeof SalesforceOAuthStatusResponseSchema>;

export const SalesforceOAuthDisconnectResponseSchema = z.object({
	success: z.boolean(),
});

export type SalesforceOAuthDisconnectResponse = z.infer<
	typeof SalesforceOAuthDisconnectResponseSchema
>;

// ── Sync logs & resync ───────────────────────────────────────────────

export enum SalesforceSyncStatus {
	PENDING = "PENDING",
	IN_PROGRESS = "IN_PROGRESS",
	SUCCESS = "SUCCESS",
	PARTIAL = "PARTIAL",
	FAILED = "FAILED",
}

export const SalesforceSyncLogSchema = z.object({
	id: z.number(),
	entityType: z.nativeEnum(SalesforceEntityType),
	status: z.nativeEnum(SalesforceSyncStatus),
	// SQL aggregates arrive as strings (web §6) — coerced so a count is a real number.
	recordsSynced: z.coerce.number().optional(),
	recordsFailed: z.coerce.number().optional(),
	errors: z.array(z.record(z.string(), z.unknown())).nullish(),
	/** Salesforce's machine-readable code, e.g. `DUPLICATES_DETECTED`. */
	errorCode: z.string().nullish(),
	errorFields: z.array(z.string()).nullish(),
	startedAt: z.string().nullish(),
	completedAt: z.string().nullish(),
	createdAt: z.string().optional(),
});

export type SalesforceSyncLog = z.infer<typeof SalesforceSyncLogSchema>;

export const SalesforceSyncLogsResponseSchema = z.object({
	logs: z.array(SalesforceSyncLogSchema),
	total: z.coerce.number().optional(),
});

export type SalesforceSyncLogsResponse = z.infer<typeof SalesforceSyncLogsResponseSchema>;

export const SalesforceResyncResponseSchema = z.object({
	queued: z.boolean(),
	queuedCount: z.coerce.number(),
	companyId: z.number().optional(),
	triggeredAt: z.string().optional(),
});

export type SalesforceResyncResponse = z.infer<typeof SalesforceResyncResponseSchema>;
